// ── Report generate / regenerate hooks (FR-REPORT) ──────────────────────────
// POST /sessions/:id/reports/generate (generate.reports = SA/Admin/coach-in-scope)
// and POST /sessions/:id/reports/regenerate (regenerate.reports = SA only). Both
// require a generated UUID Idempotency-Key. The response is the HONEST enqueue
// envelope (report:null + status pending/queued while the report-agent lane is
// closed) — the caller renders that posture, NEVER a fabricated 'generated'.
// On success we invalidate the report list + status rollup so the queue refreshes.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getCoachingReadinessServices } from "../services/coaching-service-provider";
import type { CoachingReportRegenerateRequest } from "../services/coaching-readiness-dtos";
import { coachingQueryKeys } from "./coaching-query-keys";
import { createIdempotencyKey } from "./useCoachingQueries";

async function invalidateReportReads(queryClient: ReturnType<typeof useQueryClient>) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: coachingQueryKeys.list("reports.list") }),
    queryClient.invalidateQueries({ queryKey: coachingQueryKeys.analytics.root() })
  ]);
}

export function useReportGenerateMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) =>
      getCoachingReadinessServices().reports.generate(sessionId, createIdempotencyKey(`reports-generate:${sessionId}`)),
    onSuccess: () => invalidateReportReads(queryClient)
  });
}

export function useReportRegenerateMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, body }: { sessionId: string; body?: CoachingReportRegenerateRequest }) =>
      getCoachingReadinessServices().reports.regenerate(
        sessionId,
        body ?? { confirm: true },
        createIdempotencyKey(`reports-regenerate:${sessionId}`)
      ),
    onSuccess: () => invalidateReportReads(queryClient)
  });
}
